import type { EmissionFactorSearchContext } from '../hooks/useEmissionFactorSearchState';
import type { EmissionFactor } from '../types';

interface RankEmissionFactorsInput {
  factors: EmissionFactor[];
  context?: EmissionFactorSearchContext;
  usedFactorIds?: Set<string>;
  query?: string;
}

function normalize(value: string | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

function computeContextScore(
  factor: EmissionFactor,
  context: EmissionFactorSearchContext | undefined
): number {
  if (!context) return 0;

  let score = 0;

  if (context.scope && factor.scope === context.scope) {
    score += 30;
  }

  const contextRegion = normalize(context.region);
  if (contextRegion && normalize(factor.region) === contextRegion) {
    score += 50;
  }

  if (typeof context.year === 'number' && Number.isFinite(context.year)) {
    const yearDiff = Math.abs((factor.year || 0) - context.year);
    score += Math.max(0, 25 - Math.min(25, yearDiff * 4));
  }

  const contextActivityId = normalize(context.activityId);
  if (contextActivityId && normalize(factor.activityId) === contextActivityId) {
    score += 80;
  }

  const contextActivityLabel = normalize(context.activityLabel);
  if (contextActivityLabel) {
    const haystack = `${normalize(factor.name)} ${normalize(factor.activityId)} ${normalize(factor.description)}`;
    if (haystack.includes(contextActivityLabel)) {
      score += 20;
    }
  }

  return score;
}

function computeQueryScore(factor: EmissionFactor, query: string | undefined): number {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return 0;

  const name = normalize(factor.name);
  const activityId = normalize(factor.activityId);
  const description = normalize(factor.description);

  let score = 0;
  if (name.startsWith(normalizedQuery)) score += 18;
  if (name.includes(normalizedQuery)) score += 12;
  if (activityId.includes(normalizedQuery)) score += 8;
  if (description.includes(normalizedQuery)) score += 4;
  return score;
}

function compareFallback(a: EmissionFactor, b: EmissionFactor): number {
  if (a.year !== b.year) return (b.year || 0) - (a.year || 0);
  return a.name.localeCompare(b.name);
}

export function rankEmissionFactors({
  factors,
  context,
  usedFactorIds,
  query,
}: RankEmissionFactorsInput): EmissionFactor[] {
  const usedIds = usedFactorIds ?? new Set<string>();
  return [...factors].sort((a, b) => {
    const aUsed = usedIds.has(a.id);
    const bUsed = usedIds.has(b.id);
    if (aUsed !== bUsed) return bUsed ? 1 : -1;

    const aScore = computeContextScore(a, context) + computeQueryScore(a, query);
    const bScore = computeContextScore(b, context) + computeQueryScore(b, query);
    if (aScore !== bScore) return bScore - aScore;

    return compareFallback(a, b);
  });
}
